// 委派子樹挑選器（SPEC #org-tree-ui 第 3 點 o15、ux-2-1）：GET /v1/orgs/{D}/delegations/{d}/nodes。
// 只有名稱、層級名稱與結構數，不含影像 / 事件 / 在線狀態；指派者本身不需要任何資料權；被中斷繼承的子孫不出現（伺服器就不回）。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { OrgRef } from '../../lib/planeApi';
import { treeApi, type DelegatedNodeItem, type Delegation, type Subtree } from '../../lib/orgTreeApi';
import { levelText } from '../../lib/levels';

export interface PickedNode {
  node_id: string;
  name: string;
  subtree?: Subtree;
}

export function DelegatedNodePicker({
  org,
  delegation,
  value,
  onPick,
  devices,
  onAdd,
}: {
  org: OrgRef;
  delegation: Pick<Delegation, 'delegation_id' | 'from_org_name'>;
  value?: string;
  onPick?(n: PickedNode): void;
  /** 檢視 / 群組編輯的來源：家戶底下另列設備（include=devices） */
  devices?: boolean;
  /** 檢視 / 群組編輯：〔加入〕 */
  onAdd?(it: DelegatedNodeItem & { home_id?: string; device_ref?: string }): void;
}) {
  return (
    <ul className="ot-tree ot-picker" role="tree" data-testid="dlg-node-picker">
      <Level org={org} dlg={delegation.delegation_id} depth={0} value={value} onPick={onPick} devices={devices} onAdd={onAdd} />
    </ul>
  );
}

function Level({
  org,
  dlg,
  parent,
  depth,
  value,
  onPick,
  devices,
  onAdd,
}: {
  org: OrgRef;
  dlg: string;
  parent?: string;
  depth: number;
  value?: string;
  onPick?(n: PickedNode): void;
  devices?: boolean;
  onAdd?(it: DelegatedNodeItem & { device_ref?: string }): void;
}) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['ot', org.org_id, 'dlg-nodes', dlg, parent ?? '', !!devices],
    queryFn: () => treeApi.delegatedNodes(org, dlg, { parent, include: devices ? 'devices' : undefined, limit: 1000 }),
  });
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  if (q.isLoading) return <li className="muted small">{t('common.loading')}</li>;
  if (q.isError) return <li className="error small">{t('common.error')}</li>;
  return (
    <>
      {(q.data?.items ?? []).map((it) => {
        const key = it.node_id ?? it.home_id ?? it.name;
        const isOpen = open.has(key);
        const canOpen = it.kind === 'node' ? it.has_children !== false : !!devices && (it.devices?.length ?? 0) > 0;
        return (
          <li key={key} role="treeitem" aria-expanded={canOpen ? isOpen : undefined}>
            <div className="ot-row" style={{ paddingLeft: depth * 16 }}>
              {canOpen ? (
                <button className="ot-twisty" aria-label={isOpen ? t('ot.collapse') : t('ot.expand')} onClick={() => setOpen((s) => toggle(s, key))}>
                  {isOpen ? '▾' : '▸'}
                </button>
              ) : (
                <span className="ot-twisty" />
              )}
              {it.kind === 'node' && onPick ? (
                <label className="row-inline">
                  <input type="radio" name={'dlg-pick-' + dlg} checked={value === it.node_id} onChange={() => onPick({ node_id: it.node_id!, name: it.name, subtree: it.subtree })} />
                  {it.name}
                </label>
              ) : (
                <span>{it.name}</span>
              )}
              {it.level_label && <span className="ot-chip">{levelText(t, it.level_label)}</span>}
              {it.subtree && <span className="muted small">{t('ot.n_homes_devices', { homes: it.subtree.homes, devices: it.subtree.devices })}</span>}
              {onAdd && (
                <button className="btn btn-sm ot-add" onClick={() => onAdd(it)} aria-label={t('ot.add_ref', { name: it.name })}>
                  ＋
                </button>
              )}
            </div>
            {isOpen && it.kind === 'node' && (
              <ul role="group">
                <Level org={org} dlg={dlg} parent={it.node_id} depth={depth + 1} value={value} onPick={onPick} devices={devices} onAdd={onAdd} />
              </ul>
            )}
            {isOpen && it.kind === 'home' && (
              <ul role="group">
                {(it.devices ?? []).map((d) => (
                  <li key={d.device_ref} role="treeitem">
                    <div className="ot-row" style={{ paddingLeft: (depth + 1) * 16 }}>
                      <span className="ot-twisty" />
                      <span>{d.display_name}</span>
                      {onAdd && (
                        <button className="btn btn-sm ot-add" onClick={() => onAdd({ ...it, device_ref: d.device_ref, name: d.display_name })} aria-label={t('ot.add_ref', { name: d.display_name })}>
                          ＋
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </>
  );
}

export function toggle(s: Set<string>, k: string): Set<string> {
  const n = new Set(s);
  if (n.has(k)) n.delete(k);
  else n.add(k);
  return n;
}
